import React, { useState, useEffect } from 'react';
import { generateXmlFromDisclosure, validateDisclosureData } from '../utils/XmlUtils';
import { getCustomers } from '../services/apiCustomers.ts';
import { getVendors } from '../services/IngredientsService';
import azureStorageService from '../services/azureStorageService';
import './XmlGenerator.css';

const XmlGenerator = ({ wellData, chemicalData, waterSources, onBack }) => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [xmlContent, setXmlContent] = useState('');
  const [showXml, setShowXml] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');

  // Fetch customers and vendors on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customersData] = await Promise.all([
          getCustomers(),
          getVendors()
        ]);
        setCustomers(customersData);
        console.log('Customers data:', customersData);
        
        // Auto-select customer if wellData has customer_id
        if (wellData.customer_id && customersData.length > 0) {
          const matchedCustomer = customersData.find(c => c.id === wellData.customer_id);
          if (matchedCustomer) {
            setSelectedCustomerId(wellData.customer_id.toString());
            console.log(`Auto-selected customer: ${matchedCustomer.name} (ID: ${wellData.customer_id})`);
          } else {
            console.warn(`Customer ID ${wellData.customer_id} not found in customers list`);
            setError(`Associated customer (ID: ${wellData.customer_id}) not found. Please select a customer manually.`);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to fetch customer or vendor data. Some information may not be displayed correctly.');
      }
    };

    fetchData();
  }, [wellData.customer_id]);

  const handleGenerateXml = async () => {
    try {
      console.log('=== XmlGenerator Debug Info ===');
      console.log('wellData.waterSource:', wellData.waterSource);
      console.log('waterSources prop:', waterSources);
      console.log('waterSources type:', typeof waterSources);
      console.log('waterSources JSON:', JSON.stringify(waterSources, null, 2));
      
      // Ensure chemical data is properly formatted
      const sanitizedChemicalData = chemicalData.map(chemical => ({
        ...chemical,
        // Ensure supplier is a string
        supplier: typeof chemical.supplier === 'string' ? chemical.supplier : 
                (chemical.supplier ? String(chemical.supplier) : ''),
        // Ensure other string fields are strings
        tradeName: typeof chemical.tradeName === 'string' ? chemical.tradeName : 
                  (chemical.tradeName ? String(chemical.tradeName) : ''),
        purpose: typeof chemical.purpose === 'string' ? chemical.purpose : 
                (chemical.purpose ? String(chemical.purpose) : ''),
        ingredientName: typeof chemical.ingredientName === 'string' ? chemical.ingredientName : 
                      (chemical.ingredientName ? String(chemical.ingredientName) : ''),
        casNumber: typeof chemical.casNumber === 'string' ? chemical.casNumber : 
                  (chemical.casNumber ? String(chemical.casNumber) : ''),
        comments: typeof chemical.comments === 'string' ? chemical.comments : 
                (chemical.comments ? String(chemical.comments) : '')
      }));
      
      // Create a copy of wellData to add the operator number if needed
      const wellDataWithOperator = { ...wellData };
      
      // FIXED: Always ensure waterSources is properly set
      // Use waterSources prop if available, otherwise use wellData.waterSource
      if (waterSources) {
        wellDataWithOperator.waterSource = waterSources;
      } else if (wellData.waterSource) {
        wellDataWithOperator.waterSource = wellData.waterSource;
      }
      
      console.log('XmlGenerator - Final wellDataWithOperator.waterSource:', wellDataWithOperator.waterSource);
      
      // Check if wellData has a customer field that matches a customer name
      if (wellData.customer && customers.length > 0) {
        console.log(wellData)
        console.log(`Looking for customer: ${wellData.customer}, customer id: ${wellData.customer_id}`);
        console.log("customers:", customers);
        const matchedCustomer = customers.find(c => c.id === wellData.customer_id);
        
        if (matchedCustomer) {
          console.log(`Found matching customer: ${matchedCustomer.name} with operator number: ${matchedCustomer.operator_number}`);
          wellDataWithOperator.operatorNumber = matchedCustomer.operator_number;
        } else {
          console.log(`No matching customer found for: ${wellData.customer}`);
        }
      } else {
        console.log('No customer information available or customers not loaded yet');
      }
      
      // Validate data before generating XML
      const validation = validateDisclosureData(wellDataWithOperator, sanitizedChemicalData);
      
      if (!validation.success) {
        setError(validation.error);
        setSuccess('');
        return;
      }
      
      // Check if any proprietary chemicals don't have vendor information
      const proprietaryWithoutVendor = sanitizedChemicalData.filter(
        chem => chem.casNumber === 'Proprietary' && !chem.vendorId
      );
      
      if (proprietaryWithoutVendor.length > 0) {
        setError('Some proprietary chemicals do not have vendor information. Please add vendor information in the Ingredients Management section.');
        setSuccess('');
        return;
      }
      
      // Generate XML
      const xml = await generateXmlFromDisclosure(wellDataWithOperator, sanitizedChemicalData);
      setXmlContent(xml);
      setSuccess('XML generated successfully. You can now download the file.');
      setError('');
      setShowXml(true);
    } catch (error) {
      console.error('Error generating XML:', error);
      setError(`Failed to generate XML: ${error.message}`);
      setSuccess('');
    }
  };

  const handleDownloadXml = () => {
    try {
      // Create a blob with the XML content
      const blob = new Blob([xmlContent], { type: 'application/xml' });
      
      // Create a download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${wellData.wellName} Chemical Disclosure.xml`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setSuccess(`XML file "${wellData.wellName} Chemical Disclosure.xml" has been downloaded.`);
    } catch (error) {
      console.error('Error downloading XML:', error);
      setError(`Failed to download XML: ${error.message}`);
      setSuccess('');
    }
  };

  const handleSaveToAzure = async () => {
    if (!xmlContent) {
      setError('No XML content to save. Please generate XML first.');
      return;
    }

    // Validate customer selection
    if (!selectedCustomerId) {
      setError('Please select a customer before saving the XML file.');
      return;
    }

    try {
      setIsSaving(true);
      setError('');
      
      // Find the selected customer
      const selectedCustomer = customers.find(c => c.id.toString() === selectedCustomerId);
      if (!selectedCustomer) {
        throw new Error('Selected customer not found');
      }
      
      // Create metadata from well data
      const metadata = {
        wellName: wellData.wellName || 'Unknown Well',
        operator: wellData.customer || wellData.operatorName || 'Unknown Operator',
        apiNumber: wellData.apiNumber || '',
        state: wellData.state || '',
        county: wellData.county || '',
        date: new Date().toISOString(),
        customerId: selectedCustomerId,
        customerName: selectedCustomer.name
      };
      
      // Save the XML file to Azure Blob Storage
      const result = await azureStorageService.saveXmlFile(
        `${wellData.wellName} Chemical Disclosure.xml`, 
        xmlContent,
        metadata
      );
      
      if (result.success) {
        setSuccess(`XML file "${wellData.wellName} Chemical Disclosure.xml" has been saved to cloud storage.`);
      } else {
        throw new Error('Failed to save file to cloud storage');
      }
    } catch (error) {
      console.error('Error saving XML to Azure:', error);
      
      // Check if it's a connection string error
      if (error.message.includes('Azure Storage is not initialized')) {
        setError('Azure Storage is not configured. Please add your Azure Storage connection string to the .env file.');
      } else {
        setError(`Failed to save XML to cloud storage: ${error.message}`);
      }
      
      setSuccess('');
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewInBrowser = () => {
    try {
      // Create a blob with the XML content
      const blob = new Blob([xmlContent], { type: 'application/xml' });
      
      // Create a URL for the blob
      const url = URL.createObjectURL(blob);
      
      // Open in a new tab
      window.open(url, '_blank');
      
      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Error viewing XML:', error);
      setError(`Failed to view XML: ${error.message}`);
      setSuccess('');
    }
  };

  return (
    <div className="xml-generator">
      <div className="xml-generator-header">
        <h2>XML Generator</h2>
        <p>Generate and download XML for FracFocus submission</p>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <div className="xml-generator-content">        
        <div className="customer-selection">
          {wellData.customer_id && selectedCustomerId ? (
            <div className="customer-info">
              <label>Associated Customer:</label>
              <div className="customer-display">
                <span className="customer-name">
                  {customers.find(c => c.id.toString() === selectedCustomerId)?.name || 'Unknown Customer'}
                </span>
                <span className="customer-id">(ID: {wellData.customer_id})</span>
              </div>
              <div className="customer-change">
                <button 
                  type="button" 
                  className="change-customer-btn"
                  onClick={() => setSelectedCustomerId('')}
                >
                  Change Customer
                </button>
              </div>
            </div>
          ) : (
            <div className="customer-selection-dropdown">
              <label htmlFor="customerSelect">Associate with Customer (required):</label>
              <select
                id="customerSelect"
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                className="customer-select"
                required
              >
                <option value="">-- Select a Customer --</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id.toString()}>
                    {customer.name}
                  </option>
                ))}
              </select>
              {!selectedCustomerId && showXml && (
                <div className="customer-warning">
                  You must select a customer before saving the XML file.
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="well-summary">
          <h3>Well Summary</h3>
          <div className="well-summary-grid">
            <div className="summary-item">
              <span className="summary-label">Well Name:</span>
              <span className="summary-value">{wellData.wellName}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">API Number:</span>
              <span className="summary-value">{wellData.apiNumber}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Start Date:</span>
              <span className="summary-value">{new Date(wellData.startDate).toLocaleString()}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">End Date:</span>
              <span className="summary-value">{new Date(wellData.endDate).toLocaleString()}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">State:</span>
              <span className="summary-value">{wellData.state}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">County:</span>
              <span className="summary-value">{wellData.county}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">TVD:</span>
              <span className="summary-value">{wellData.tvd} ft</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Total Clean:</span>
              <span className="summary-value">{wellData.totalClean} gal</span>
            </div>
          </div>
        </div>
        
        <div className="chemical-summary">
          <h3>Chemical Summary</h3>
          <p>Total Chemicals: {chemicalData.length}</p>
          <p>MSDS Components: {chemicalData.filter(chem => chem.comments !== 'Non-MSDS Component').length}</p>
          <p>Non-MSDS Components: {chemicalData.filter(chem => chem.comments === 'Non-MSDS Component').length}</p>
        </div>
        
        {showXml && (
          <div className="xml-preview">
            <h3>XML Preview</h3>
            <pre className="xml-content">{xmlContent}</pre>
          </div>
        )}
        
        <div className="xml-generator-actions">
          <button className="back-button" onClick={onBack}>
            Back to Chemical Disclosure
          </button>
          
          <button className="generate-button" onClick={handleGenerateXml}>
            Generate XML
          </button>
          
          {showXml && (
            <>
              <button className="download-button" onClick={handleDownloadXml}>
                Download XML
              </button>
              
              <button 
                className="save-button" 
                onClick={handleSaveToAzure}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save to Cloud'}
              </button>
              
              <button className="view-button" onClick={handleViewInBrowser}>
                View in Browser
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default XmlGenerator;
